import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex items-center justify-center w-[60px] h-[60px] rounded-full bg-anykrowd-gray/30" }

import { onMounted, onUnmounted, ref, watch } from 'vue';
import { Capacitor, App as CapApp } from '@capacitor/core';
import { IonApp, IonRouterOutlet, IonIcon, IonProgressBar } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { FacebookLogin } from '@whiteguru/capacitor-plugin-facebook-login';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import { useApp } from '@/shared/composables/useApp';
import useStorageService from '@/shared/composables/useStorageService';
import { useCacheImage } from '@/shared/composables/useCacheImage';
import { useSoftUpdate } from '@/shared/components/soft-update/composables/useSoftUpdate';


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const { t, locale } = useI18n();
const assetStore = useAssetsStore();
const { get } = useStorageService();
const { loadImage } = useCacheImage();
const { handleDebugMode, handleNotNative, handleSubscribeToConfig, handleSubscribeToStyles, hideOfflineMessage, initAppLinksListener, listenForNotifications, loadApp, loadTenantDetails, tenantConfigLoaded, tenantStylesLoaded } = useApp();
const backgroundUrl = ref();

watch(
	assetStore,
	async (assetStore) => {
		if (assetStore.backgroundUrl) {
			let backgroundImage = assetStore.backgroundUrl.split('/').pop();
			backgroundImage = backgroundImage ?? 'bg';
			backgroundUrl.value = await loadImage('background', backgroundImage, assetStore.backgroundUrl);
		}
	},
	{ immediate: true },
);

const FACEBOOK_APP_ID = process.env.VUE_APP_FACEBOOK_APP_ID;
const GOOGLE_ANALYTCS_TRACKING_CODE = process.env.VUE_APP_GOOGLE_ANALYTCS_TRACKING_CODE;
const ENVIRONMENT_DEBUG = process.env.VUE_APP_ENVIRONMENT_DEBUG;

const isNative = Capacitor.isNativePlatform();
const { checkForUpdates, applyPendingUpdate } = useSoftUpdate();

const checkWebVersion = async () => {
	if (isNative) return;
	try {
		const baseUrl = window.location.origin;
		const res = await fetch(`${baseUrl}/version.json?t=${Date.now()}`);
		if (!res.ok) return;
		const serverVersion = await res.json();
		const currentVersion = process.env.VUE_APP_BUILD_ID;
		if (currentVersion && serverVersion.buildId && serverVersion.buildId !== currentVersion) {
			console.log('[SilentUpdate] New web version available, reloading...');
			window.location.reload();
		}
	} catch {
		// offline or unreachable, ignore silently
	}
};

const handleVisibilityChange = () => {
	if (!document.hidden) {
		checkWebVersion();
	}
};

const handleResume = () => {
	if (isNative && ENVIRONMENT_DEBUG === 'false') {
		applyPendingUpdate();
		checkForUpdates();
	}
};

handleDebugMode();

if (!isNative) {
	handleNotNative();
}

onMounted(async () => {
	await handleSubscribeToConfig();
	await handleSubscribeToStyles();

	if (isNative) {
		await loadTenantDetails();

		window.assetUrl = '/';
		window.googleAnalyticsTrackingCode = GOOGLE_ANALYTCS_TRACKING_CODE!;

		console.log('Updater: Notifying app ready.');
		CapacitorUpdater.notifyAppReady();

		if (ENVIRONMENT_DEBUG === 'false') {
			await applyPendingUpdate();
			checkForUpdates();
		}

		await FacebookLogin.initialize({ appId: FACEBOOK_APP_ID });
		GoogleAuth.initialize();

		initAppLinksListener();
		listenForNotifications();

		CapApp.addListener('resume', handleResume);
	} else {
		checkWebVersion();
		document.addEventListener('visibilitychange', handleVisibilityChange);
	}

	get('app_language').then((appLanguage) => {
		if (appLanguage) {
			locale.value = appLanguage;
		}
	});
});

onUnmounted(() => {
	CapApp.removeAllListeners('resume');
	document.removeEventListener('visibilitychange', handleVisibilityChange);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(loadApp) && _unref(tenantConfigLoaded) && _unref(tenantStylesLoaded))
      ? (_openBlock(), _createBlock(_unref(IonApp), {
          key: 0,
          style: _normalizeStyle(`background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${backgroundUrl.value}') no-repeat center center / cover;`)
        }, {
          default: _withCtx(() => [
            (_openBlock(), _createBlock(_unref(IonRouterOutlet), {
              key: _ctx.$route.fullPath
            })),
            _createVNode(_unref(IonProgressBar), {
              id: "app-progress-bar",
              type: "indeterminate",
              class: "fixed bottom-0 left-0 right-0",
              style: {"display":"none"}
            })
          ]),
          _: 1
        }, 8, ["style"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      style: _normalizeStyle([
			{
				'padding-top': _unref(Capacitor).isNativePlatform() ? 'env(safe-area-inset-top, 20px)' : 'pt-4',
			},
		]),
      class: "flex flex-col items-center justify-between p-4 network-status relative z-10 backdrop-blur-xl bg-white/30 text-xs text-center text-white hidden"
    }, [
      _createElementVNode("div", {
        class: "flex items-center justify-center cursor-pointer mb-4",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(hideOfflineMessage)()))
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_unref(IonIcon), {
            icon: _unref(closeOutline),
            class: "text-white text-regular"
          }, null, 8, ["icon"])
        ])
      ]),
      _createElementVNode("span", null, _toDisplayString(_unref(t)('general.connection.offline')), 1)
    ], 4)
  ], 64))
}
}

})